import { useEffect, useRef } from 'react'
import { useToast } from '../contexts/ToastContext'
import useScrapeOperationStore from '../stores/scrapeOperationStore'
import { scrapingService } from '../services/scrapingService'

const POLL_INTERVAL_MS = 15_000
const TIMEOUT_MS = 10 * 60 * 1000 // 10 minutes

export function useScrapePolling() {
  const { showSuccess, showError, showInfo } = useToast()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      const { pendingOperations, removeOperation, markCompleted } =
        useScrapeOperationStore.getState()

      if (pendingOperations.length === 0) return

      for (const op of pendingOperations) {
        // Timeout check
        if (Date.now() - op.startedAt > TIMEOUT_MS) {
          removeOperation(op.operationId)
          showInfo(
            'Scraping Timeout',
            `Scraping for @${op.username} on ${op.platform} timed out. Check the Jobs tab for status.`
          )
          continue
        }

        try {
          const response = await scrapingService.getOperationStatus(op.operationId)

          if (!response.success || !response.data) continue

          const { status } = response.data

          if (status === 'completed') {
            markCompleted(op.operationId)
            showSuccess(
              'Scraping Complete',
              `@${op.username} on ${op.platform} has finished scraping.`
            )
          } else if (status === 'failed') {
            removeOperation(op.operationId)
            showError(
              'Scraping Failed',
              `Scraping for @${op.username} on ${op.platform} failed. Check the Jobs tab for details.`
            )
          }
          // 'started' status — keep polling
        } catch {
          // Network error — keep polling, don't remove
        }
      }
    }, POLL_INTERVAL_MS)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [showSuccess, showError, showInfo])
}
